import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { usePlayerList } from '@/features/squad/queries'
import { suggestPicks, PickReason, type SuggestedPick } from '@/features/squad/lib/suggestPicks'
import type { Position } from '@/types/domain'
import type { LeagueSummary } from '@/features/league/types'
import { competitionOptions } from '../lib/competitionOptions'
import { CompetitionSelect } from './CompetitionSelect'
import styles from './SuggestedPickCard.module.css'

type SuggestedPickCardProps = {
  leagues: LeagueSummary[]
}

const POSITION_CLASS: Record<Position, string | undefined> = {
  GK:  styles.posGk,
  DEF: styles.posDef,
  MID: styles.posMid,
  FWD: styles.posFwd,
}

const REASON_LABEL: Record<PickReason, string> = {
  [PickReason.Value]:  'Ótimo custo-benefício',
  [PickReason.Form]:   'Em alta',
  [PickReason.Points]: 'Pontuador',
}

function PickRow({ pick }: { pick: SuggestedPick }) {
  const { player } = pick
  return (
    <div className={styles.pickRow}>
      <span className={clsx(styles.posBadge, POSITION_CLASS[player.position])}>
        {player.position}
      </span>
      <div className={styles.pickInfo}>
        <div className={styles.pickName}>{player.displayName}</div>
        <div className={styles.pickReasons}>
          {pick.reasons.length > 0
            ? pick.reasons.map(r => (
                <span key={r} className={styles.reasonChip}>{REASON_LABEL[r]}</span>
              ))
            : <span className={styles.reasonMuted}>{player.teamName}</span>}
        </div>
      </div>
      <div className={styles.pickRight}>
        <div className={styles.pickPrice}>${player.price.toFixed(1)}</div>
        <div className={styles.pickPriceSub}>{player.teamCode}</div>
      </div>
    </div>
  )
}

export function SuggestedPickCard({ leagues }: SuggestedPickCardProps) {
  const competitions = useMemo(() => competitionOptions(leagues), [leagues])
  const [competitionId, setCompetitionId] = useState(() => competitions[0]?.id ?? '')

  const { data, isPending, isError } = usePlayerList({
    search:        '',
    position:      'ALL',
    sortBy:        'avgPoints',
    sortDir:       'desc',
    competitionId,
    teamCode:      '',
    minPrice:      null,
    maxPrice:      null,
    hideEliminated: false,
  })

  const picks = useMemo(() => {
    const players = data?.pages.flatMap(p => p.items) ?? []
    return suggestPicks(players, 3)
  }, [data])

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>Dica da Rodada</span>
        <CompetitionSelect
          competitions={competitions}
          value={competitionId}
          onChange={setCompetitionId}
        />
      </div>

      {isPending ? (
        <div className={styles.state}>Carregando sugestões…</div>
      ) : isError ? (
        <div className={styles.state}>Não foi possível carregar as sugestões.</div>
      ) : picks.length === 0 ? (
        <div className={styles.state}>Nenhuma sugestão disponível ainda.</div>
      ) : (
        <div className={styles.list}>
          {picks.map(pick => (
            <PickRow key={pick.player.playerId} pick={pick} />
          ))}
        </div>
      )}
    </div>
  )
}
